<template>
    <div ref="wrapper" class="horizontal-bar-chart">
      <ChartBarHorizontal
          ref="chart"
          class="bar-chart"
          :chart-data="chartData"
          :options="horizontalBarChartOptions"
          :width="width"
          :height="height"
      />
      <div
          v-show="labelTooltip.visible"
          class="label-tooltip"
          :style="{ left: `${labelTooltip.x}px`, top: `${labelTooltip.y}px` }"
      >
        {{ labelTooltip.text }}
      </div>
    </div>
</template>

<script>

import Vue from 'vue';
import ChartBarHorizontal from '../../../ChartBarHorizontal.vue';

const contractInteger = (num) => Vue.$numberManagement.contractInteger(num);
const numberWithComma = (num) => Vue.$numberManagement.numberWithComma(num);

export default {
  name: 'BarChart',
  components: {
    ChartBarHorizontal,
  },

  props: {
    itemData: {
      type: Array,
      default() {
        return ([]);
      },
    },

    label: {
      type: String,
      default: '',
    },

    width: {
      type: Number,
    },

    height: {
      type: Number,
    },

    barChartColor: {
      type: String,
      default: 'rgba(54, 162, 235, 1)',
    },
  },

  data() {
    return ({
      labelTooltip: {
        visible: false,
        x: 0,
        y: 0,
        text: '',
      },
      horizontalBarChartOptions: {
        legend: false,
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              callback: (value) => contractInteger(value),
            },
          }],
        },
        tooltips: {
          callbacks: {
            label(tooltipItem) {
              if (tooltipItem.value === null) {
                return '0';
              }

              return numberWithComma(tooltipItem.value);
            },
          },
        },
      },
    });
  },

  computed: {
    chartData() {
      return ({
        labels: this.itemData.map((el) => el.label),
        datasets: [{
          label: this.label,
          data: this.itemData.map((el) => el.value),
          borderColor: this.barChartColor,
          borderWidth: 1,
        }],
      });
    },
  },

  mounted() {
    this.$nextTick(() => this.bindLabelTooltip());
  },

  beforeDestroy() {
    if (this.canvas) {
      this.canvas.removeEventListener('mousemove', this.onMove);
      this.canvas.removeEventListener('mouseleave', this.onLeave);
    }
  },

  methods: {
    bindLabelTooltip() {
      // eslint-disable-next-line no-underscore-dangle
      const chart = this.$refs.chart && this.$refs.chart.$data._chart;

      if (!chart || !chart.canvas) {
        return;
      }

      this.canvas = chart.canvas;
      this.onMove = (event) => this.handleMove(event, chart);
      this.onLeave = () => { this.labelTooltip.visible = false; };

      this.canvas.addEventListener('mousemove', this.onMove);
      this.canvas.addEventListener('mouseleave', this.onLeave);
    },

    getYScale(chart) {
      return chart.scales['y-axis-0']
        || Object.values(chart.scales).find((scale) => !scale.isHorizontal());
    },

    handleMove(event, chart) {
      const yScale = this.getYScale(chart);
      const area = chart.chartArea;

      if (!yScale || !area) {
        this.labelTooltip.visible = false;

        return;
      }

      const canvasRect = this.canvas.getBoundingClientRect();
      const x = event.clientX - canvasRect.left;
      const y = event.clientY - canvasRect.top;

      // The label zone lies to the left of the plotting area.
      if (x < yScale.left || x > area.left) {
        this.labelTooltip.visible = false;

        return;
      }

      const count = this.itemData.length;
      const rowHalf = count ? (area.bottom - area.top) / count / 2 : 0;
      let found = -1;

      for (let i = 0; i < count; i += 1) {
        if (Math.abs(y - yScale.getPixelForTick(i)) <= rowHalf) {
          found = i;
          break;
        }
      }

      if (found === -1) {
        this.labelTooltip.visible = false;

        return;
      }

      const item = this.itemData[found];
      const wrapperRect = this.$refs.wrapper.getBoundingClientRect();

      this.labelTooltip.text = numberWithComma(item ? item.value : null);
      this.labelTooltip.x = event.clientX - wrapperRect.left + 12;
      this.labelTooltip.y = event.clientY - wrapperRect.top;
      this.labelTooltip.visible = true;
    },
  },
};
</script>

<style scoped>
.horizontal-bar-chart {
  position: relative;
}

.label-tooltip {
  position: absolute;
  pointer-events: none;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
  z-index: 10;
}
</style>
